import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import styles from "./PrizeSpinner.module.scss";

export type PrizeSpinnerProps = {
  drawnIndex?: number;
  options: string[];
  isLoading: boolean;
  className?: string;
  title: string;
};

export const PrizeSpinner = ({ drawnIndex, options = [], isLoading, className, title }: PrizeSpinnerProps) => {
  const [optionsHidden, setOptionsHidden] = useState(false);
  const wheel = useRef<HTMLDivElement>(null);
  const { sanitize } = dompurify;
  const shouldStopWheel = wheel.current && drawnIndex !== undefined;

  const modifiedOptions = useMemo(
    () => (options.length === 4 ? new Array(2).fill(options).flat() : options),
    [options]
  );

  useEffect(() => {
    if (wheel.current && drawnIndex !== undefined) {
      const rotationValue = `-${360 * 5 + drawnIndex * 45}deg`;
      wheel.current.style.setProperty("--rotation", rotationValue);
      setTimeout(() => {
        setOptionsHidden(true);
      }, 2000);
    }
  }, [drawnIndex]);

  if (!isLoading && drawnIndex === undefined) {
    return (
      <div
        className={classNames(styles.title)}
        dangerouslySetInnerHTML={{ __html: sanitize(title, { ALLOWED_TAGS: ["b"] }) }}
      ></div>
    );
  }

  return (
    <div className={classNames(styles.prizeSpinner, className)}>
      <div
        ref={wheel}
        data-testid="wheel"
        className={classNames(styles.wheel, {
          [styles.spin]: isLoading,
          [styles.finished]: shouldStopWheel,
        })}
      >
        {modifiedOptions.map((item, index) => (
          <div
            data-testid="wheelOption"
            style={{ "--index": index } as CSSProperties}
            className={classNames(styles.option, {
              [styles.hidden]: optionsHidden && index !== drawnIndex,
            })}
            key={item + index}
          >
            <div
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: sanitize(item, { ALLOWED_TAGS: ["b"] }) }}
            ></div>
          </div>
        ))}
      </div>
    </div>
  );
};
